import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, IconButton, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import RefreshIcon from '@material-ui/icons/Refresh'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import RestoreIcon from '@material-ui/icons/Restore'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SydButton from '../../../../../../commonDesign/Button'
import { useAnalyzeDqlQuery } from '../../../../../../../api/denali'
import DqlEditor from '../../components/configurationManagement/DqlEditor'

const MONO = "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"

const useStyles = makeStyles(() => ({
  paper: {
    minWidth: '1100px',
    maxWidth: '98vw',
    width: '98vw',
    height: '90vh',
    maxHeight: '90vh',
    margin: 0
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 18px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    flexShrink: 0
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  meta: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: MONO,
    border: '1px solid transparent'
  },
  statusIdle: {
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)',
    borderColor: 'var(--color-neutral-200)'
  },
  statusAnalyzing: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)',
    borderColor: 'var(--color-primary-200, #bfdbfe)'
  },
  statusOk: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)',
    borderColor: 'var(--color-success-200, #bbf7d0)'
  },
  statusWarn: {
    backgroundColor: 'var(--color-warning-050, #fffbeb)',
    color: 'var(--color-warning-700, #b45309)',
    borderColor: 'var(--color-warning-200, #fde68a)'
  },
  statusError: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)',
    borderColor: 'var(--color-danger-200, #fecaca)'
  },
  statusDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: 'currentColor'
  },
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    minHeight: 0
  },
  editorPane: {
    width: '50%',
    minWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--color-neutral-200)',
    backgroundColor: 'white'
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-025, #fafafa)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  paneHeaderActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  paneHint: {
    fontSize: '11px',
    fontWeight: 400,
    textTransform: 'none',
    letterSpacing: 'normal',
    color: 'var(--color-neutral-500)'
  },
  editorBody: {
    flex: 1,
    overflow: 'auto',
    padding: '12px 16px'
  },
  diagnosticsPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'var(--color-neutral-025, #fafafa)'
  },
  diagnosticsBody: {
    flex: 1,
    overflow: 'auto',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '8px'
  },
  statCard: {
    padding: '8px 10px',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-200)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  statCardLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  statCardValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--color-neutral-900)',
    fontFamily: MONO
  },
  statCardWarn: {
    color: 'var(--color-warning-700, #b45309)'
  },
  statCardOk: {
    color: 'var(--color-success-700, #15803d)'
  },
  errorPanel: {
    border: '1px solid var(--color-danger-200, #fecaca)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  errorTitle: {
    fontSize: '12px',
    fontWeight: 700,
    color: 'var(--color-danger-700, #b91c1c)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  errorList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '12px',
    color: 'var(--color-danger-800, #991b1b)',
    fontFamily: MONO
  },
  errorPhase: {
    display: 'inline-block',
    padding: '0 5px',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: MONO,
    color: 'var(--color-danger-700, #b91c1c)',
    backgroundColor: 'rgba(254, 202, 202, 0.6)',
    borderRadius: '3px',
    marginRight: '6px'
  },
  warnPanel: {
    border: '1px solid var(--color-warning-200, #fde68a)',
    backgroundColor: 'var(--color-warning-050, #fffbeb)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '12px',
    color: 'var(--color-warning-800, #92400e)'
  },
  section: {
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: 'var(--color-neutral-050)',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-neutral-800)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  sectionHeaderClosed: {
    borderBottom: 'none'
  },
  sectionTitle: {
    flex: 1
  },
  sectionCount: {
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: MONO,
    color: 'var(--color-neutral-500)',
    backgroundColor: 'white',
    padding: '1px 6px',
    borderRadius: '3px',
    border: '1px solid var(--color-neutral-200)'
  },
  sectionBody: {
    padding: '10px 12px',
    fontSize: '12px',
    color: 'var(--color-neutral-700)'
  },
  chipGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '12px',
    backgroundColor: 'var(--color-neutral-100)',
    fontSize: '11px',
    fontFamily: MONO,
    color: 'var(--color-neutral-700)'
  },
  chipMuted: {
    color: 'var(--color-neutral-500)'
  },
  chipAggregate: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  catalogList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  catalogItem: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  catalogHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    backgroundColor: 'var(--color-neutral-025, #fafafa)',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  catalogTable: {
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  catalogAlias: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  catalogDatapoints: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 1fr',
    gap: '4px 12px',
    padding: '8px 10px',
    fontSize: '11px',
    fontFamily: MONO,
    color: 'var(--color-neutral-700)',
    backgroundColor: 'white'
  },
  datapointName: {
    color: 'var(--color-neutral-900)',
    fontWeight: 600
  },
  datapointType: {
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    fontSize: '10px',
    letterSpacing: '0.04em'
  },
  datapointDisplay: {
    color: 'var(--color-neutral-600)',
    fontFamily: 'inherit'
  },
  unextractedRow: {
    display: 'grid',
    gridTemplateColumns: '120px 80px 1fr',
    gap: '8px',
    padding: '4px 0',
    fontSize: '11px',
    fontFamily: MONO,
    borderBottom: '1px solid var(--color-neutral-100)',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  unextractedExpr: {
    color: 'var(--color-neutral-700)',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  recList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  recItem: {
    padding: '8px 10px',
    borderLeft: '3px solid var(--color-warning-400, #fbbf24)',
    backgroundColor: 'var(--color-warning-050, #fffbeb)',
    borderRadius: '0 4px 4px 0',
    fontSize: '12px'
  },
  recPriority: {
    display: 'inline-block',
    padding: '0 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 700,
    fontFamily: MONO,
    textTransform: 'uppercase',
    marginRight: '6px'
  },
  recPriorityHigh: {
    backgroundColor: 'var(--color-danger-100, #fee2e2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  recPriorityMedium: {
    backgroundColor: 'var(--color-warning-100, #fef3c7)',
    color: 'var(--color-warning-700, #b45309)'
  },
  recPriorityLow: {
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-700)'
  },
  pre: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-800)',
    backgroundColor: 'var(--color-neutral-025, #fafafa)',
    padding: '8px 10px',
    borderRadius: '4px',
    border: '1px solid var(--color-neutral-200)',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '320px',
    overflow: 'auto'
  },
  emptyHint: {
    fontSize: '11px',
    fontStyle: 'italic',
    color: 'var(--color-neutral-500)'
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderTop: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  }
}))

const COLLAPSE_STORAGE_KEY = 'studio:dqlDiagnostics:openSections:v1'

function useCollapseState (defaults) {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return defaults
    try {
      const raw = window.localStorage?.getItem(COLLAPSE_STORAGE_KEY)
      if (!raw) return defaults
      const parsed = JSON.parse(raw)
      return { ...defaults, ...parsed }
    } catch {
      return defaults
    }
  })
  useEffect(() => {
    try {
      window.localStorage?.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(open))
    } catch {
      // ignore quota / disabled storage
    }
  }, [open])
  const toggle = useCallback((key) => {
    setOpen(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])
  return [open, toggle]
}

function Section ({ title, count, isOpen, onToggle, children }) {
  const classes = useStyles()
  return (
    <div className={classes.section}>
      <div
        className={`${classes.sectionHeader} ${!isOpen ? classes.sectionHeaderClosed : ''}`}
        onClick={onToggle}
      >
        {isOpen ? <ExpandMoreIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
        <span className={classes.sectionTitle}>{title}</span>
        {count != null && <span className={classes.sectionCount}>{count}</span>}
      </div>
      {isOpen && <div className={classes.sectionBody}>{children}</div>}
    </div>
  )
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node
}

function CatalogMatch ({ match }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const fqn = match.schema ? `${match.schema}.${match.tableCode}` : match.tableCode
  return (
    <div className={classes.catalogItem}>
      <div className={classes.catalogHeader} onClick={() => setOpen(prev => !prev)}>
        {open ? <ExpandMoreIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
        <span className={classes.catalogTable}>{fqn}</span>
        <span className={classes.catalogAlias}>{match.viewName ? `· ${match.viewName}` : ''}</span>
        <span className={classes.sectionCount} style={{ marginLeft: 'auto' }}>
          {match.datapointCount} datapoint{match.datapointCount === 1 ? '' : 's'}
        </span>
      </div>
      {open && match.datapoints?.length > 0 && (
        <div className={classes.catalogDatapoints}>
          {match.datapoints.map((dp, idx) => (
            <React.Fragment key={`${dp.accessor}-${idx}`}>
              <span className={classes.datapointName}>{dp.accessor}</span>
              <span className={classes.datapointType}>{dp.type || '—'}</span>
              <span className={classes.datapointDisplay}>{dp.displayName || dp.description || ''}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

CatalogMatch.propTypes = {
  match: PropTypes.shape({
    schema: PropTypes.string,
    tableCode: PropTypes.string,
    viewName: PropTypes.string,
    datapointCount: PropTypes.number,
    datapoints: PropTypes.array
  }).isRequired
}

function buildMarkdownReport ({ purposeCode, dql, result }) {
  const lines = []
  lines.push('# DQL Diagnostic Report')
  if (purposeCode) lines.push(`Purpose: \`${purposeCode}\``)
  lines.push('')
  lines.push('## Source DQL')
  lines.push('```sql')
  lines.push(dql || '(empty)')
  lines.push('```')
  if (!result) {
    lines.push('')
    lines.push('_No analysis results yet._')
    return lines.join('\n')
  }
  lines.push('')
  lines.push('## Status')
  lines.push(`- Pipeline: ${result.pipelineInfo?.usedMainParser ? 'main parser' : 'fallback'}`)
  lines.push(`- Errors: ${result.errors?.length || 0}`)
  lines.push(`- Warnings: ${result.warnings?.length || 0}`)
  lines.push(`- Tables: ${result.tableReferences?.length || 0}`)
  lines.push(`- Columns: ${result.columnReferences?.length || 0}`)
  lines.push(`- Catalog matches: ${result.catalogContext?.matchedViews?.length || 0}`)
  if (result.astColumnAnalysis) {
    lines.push(`- Extracted: ${result.astColumnAnalysis.extractedCount}`)
    lines.push(`- Unextracted: ${result.astColumnAnalysis.unextractedCount}`)
  }
  if (result.errors?.length) {
    lines.push('')
    lines.push('## Errors')
    for (const err of result.errors) {
      lines.push(`- **${err.phase || 'unknown'}**: ${err.message}`)
      if (err.details && err.details !== err.message) lines.push(`  ${err.details}`)
    }
  }
  if (result.warnings?.length) {
    lines.push('')
    lines.push('## Warnings')
    for (const w of result.warnings) {
      lines.push(`- **${w.phase || 'unknown'}**: ${w.message}`)
    }
  }
  if (result.tableReferences?.length) {
    lines.push('')
    lines.push('## Tables')
    for (const t of result.tableReferences) {
      const fqn = t.schema ? `${t.schema}.${t.tableCode}` : t.tableCode
      lines.push(`- \`${fqn}\` AS \`${t.alias || ''}\``)
    }
  }
  if (result.columnReferences?.length) {
    lines.push('')
    lines.push(`## Columns (${result.columnReferences.length})`)
    for (const c of result.columnReferences) {
      const t = c.table ? `${c.table}.` : ''
      const a = c.alias && c.alias !== c.column ? ` AS \`${c.alias}\`` : ''
      const tag = c.isAggregate ? ' (aggregate)' : c.isFunction ? ' (function)' : ''
      lines.push(`- \`${t}${c.column}\`${a}${tag}`)
    }
  }
  if (result.catalogContext?.matchedViews?.length) {
    lines.push('')
    lines.push('## Catalog Matches')
    for (const v of result.catalogContext.matchedViews) {
      const fqn = v.schema ? `${v.schema}.${v.tableCode}` : v.tableCode
      lines.push(`- \`${fqn}\` (${v.datapointCount || 0} datapoints)`)
    }
  }
  if (result.astColumnAnalysis?.unextractedCount > 0) {
    lines.push('')
    lines.push('## Unextracted Columns')
    for (const c of result.astColumnAnalysis.allSelectColumns.filter(c => !c.extracted)) {
      lines.push(`- \`${c.expressionType}\` (${c.alias || '—'}) — ${c.rawExpression}`)
      if (c.extractionReason) lines.push(`  _${c.extractionReason}_`)
    }
  }
  if (result.recommendations?.length) {
    lines.push('')
    lines.push('## Recommendations')
    for (const r of result.recommendations) {
      lines.push(`- **[${(r.priority || '').toUpperCase()}]** ${r.description}`)
      if (r.file) lines.push(`  \`${r.file}\``)
    }
  }
  if (result.pipelineInfo?.sql) {
    lines.push('')
    lines.push('## Compiled SQL')
    lines.push('```sql')
    lines.push(result.pipelineInfo.sql)
    lines.push('```')
  }
  return lines.join('\n')
}

function DqlDiagnosticsModal ({ open, onClose, purposeCode, savedDql }) {
  const classes = useStyles()
  const { mutateAsync: analyze, isLoading: isAnalyzing } = useAnalyzeDqlQuery()

  const [dql, setDql] = useState(savedDql || '')
  const [result, setResult] = useState(null)
  const [analyzeError, setAnalyzeError] = useState(null)
  const [lastAnalyzedDql, setLastAnalyzedDql] = useState('')
  const debounceRef = useRef(null)

  const [openSections, toggleSection] = useCollapseState({
    catalog: true,
    columns: true,
    tables: true,
    unextracted: true,
    recommendations: true,
    sql: false,
    ast: false
  })

  // Re-seed each open so previous edits don't leak across purposes.
  useEffect(() => {
    if (open) {
      setDql(savedDql || '')
      setResult(null)
      setAnalyzeError(null)
      setLastAnalyzedDql('')
    }
  }, [open, savedDql, purposeCode])

  const runAnalyze = useCallback(async (text) => {
    if (!text || !text.trim()) {
      setResult(null)
      setAnalyzeError(null)
      return
    }
    try {
      const data = await analyze({ dql: text })
      setResult(data)
      setLastAnalyzedDql(text)
      setAnalyzeError(null)
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Analysis failed'
      setAnalyzeError(msg)
      setResult(null)
    }
  }, [analyze])

  // Debounced re-analysis; first run after open is immediate.
  useEffect(() => {
    if (!open) return undefined
    if (!dql || dql === lastAnalyzedDql) return undefined
    const delay = result == null ? 0 : 500
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      runAnalyze(dql)
    }, delay)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open, dql, lastAnalyzedDql, result, runAnalyze])

  const handleResetToSaved = useCallback(() => {
    setDql(savedDql || '')
  }, [savedDql])

  const handleCopyReport = useCallback(async () => {
    const md = buildMarkdownReport({ purposeCode, dql, result })
    try {
      await navigator.clipboard.writeText(md)
    } catch {
      // Fall back to textarea hack for browsers without clipboard permission.
      const ta = document.createElement('textarea')
      ta.value = md
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
  }, [purposeCode, dql, result])

  const editorIsDirty = dql !== (savedDql || '')
  const errorCount = result?.errors?.length || 0
  const warnCount = result?.warnings?.length || 0
  const unextractedCount = result?.astColumnAnalysis?.unextractedCount || 0

  let statusKey = 'idle'
  let statusLabel = 'Ready'
  if (isAnalyzing) {
    statusKey = 'analyzing'
    statusLabel = 'Analyzing\u2026'
  } else if (analyzeError || errorCount > 0) {
    statusKey = 'error'
    statusLabel = analyzeError ? 'Analyzer error' : `${errorCount} error${errorCount === 1 ? '' : 's'}`
  } else if (warnCount > 0 || unextractedCount > 0) {
    statusKey = 'warn'
    statusLabel = `${warnCount + unextractedCount} issue${warnCount + unextractedCount === 1 ? '' : 's'}`
  } else if (result) {
    statusKey = 'ok'
    statusLabel = 'Clean'
  }
  const statusClass = {
    idle: classes.statusIdle,
    analyzing: classes.statusAnalyzing,
    ok: classes.statusOk,
    warn: classes.statusWarn,
    error: classes.statusError
  }[statusKey]

  const usingFallback = result?.pipelineInfo && result.pipelineInfo.usedMainParser === false

  const stats = useMemo(() => {
    if (!result) return null
    return {
      tables: result.tableReferences?.length || 0,
      columns: result.columnReferences?.length || 0,
      aggregates: (result.columnReferences || []).filter(c => c.isAggregate).length,
      catalog: result.catalogContext?.matchedViews?.length || 0,
      extracted: result.astColumnAnalysis?.extractedCount ?? 0,
      unextracted: result.astColumnAnalysis?.unextractedCount ?? 0
    }
  }, [result])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <div className={classes.title}>
            DQL Diagnostics{purposeCode ? ` · ${purposeCode}` : ''}
          </div>
          <div className={classes.meta}>
            Live re-analysis as you edit. Edits here do not save to the purpose.
          </div>
        </div>
        <span className={`${classes.statusPill} ${statusClass}`}>
          <span className={classes.statusDot} />
          {statusLabel}
        </span>
        <IconButton size='small' onClick={onClose} aria-label='Close diagnostics'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </div>

      <div className={classes.body}>
        <div className={classes.editorPane}>
          <div className={classes.paneHeader}>
            <span>Source DQL</span>
            {editorIsDirty && (
              <span className={classes.paneHint}>· edited</span>
            )}
            <div className={classes.paneHeaderActions}>
              <IconButton
                size='small'
                onClick={handleResetToSaved}
                disabled={!editorIsDirty}
                title='Reset to the purpose’s saved DQL'
                aria-label='Reset to saved DQL'
              >
                <RestoreIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                onClick={() => runAnalyze(dql)}
                disabled={isAnalyzing || !dql.trim()}
                title='Re-analyze now'
                aria-label='Re-analyze'
              >
                <RefreshIcon fontSize='small' />
              </IconButton>
            </div>
          </div>
          <div className={classes.editorBody}>
            <DqlEditor
              name='dql-diagnostics-editor'
              value={dql}
              onChange={setDql}
              minLines={20}
              maxLines={60}
            />
          </div>
          <div className={classes.toolbarRow}>
            <SydButton
              size='sm'
              variant='ghost'
              icon='copy'
              onClick={handleCopyReport}
              disabled={!result && !analyzeError}
            >
              Copy report
            </SydButton>
          </div>
        </div>

        <div className={classes.diagnosticsPane}>
          <div className={classes.paneHeader}>
            <span>Diagnostics</span>
            {usingFallback && <span className={classes.paneHint}>· using fallback parser</span>}
          </div>
          <div className={classes.diagnosticsBody}>
            {analyzeError && (
              <div className={classes.errorPanel}>
                <div className={classes.errorTitle}>Analyzer call failed</div>
                <div style={{ fontFamily: MONO, fontSize: '12px', color: 'var(--color-danger-800, #991b1b)' }}>
                  {analyzeError}
                </div>
              </div>
            )}

            {result && (
              <>
                {stats && (
                  <div className={classes.statsRow}>
                    <Stat label='Tables' value={stats.tables} />
                    <Stat label='Columns' value={stats.columns} />
                    <Stat label='Aggregates' value={stats.aggregates} />
                    <Stat label='Catalog hits' value={stats.catalog} tone={stats.catalog === 0 ? 'warn' : 'ok'} />
                    <Stat label='Extracted' value={stats.extracted} tone='ok' />
                    <Stat label='Unextracted' value={stats.unextracted} tone={stats.unextracted > 0 ? 'warn' : null} />
                  </div>
                )}

                {result.errors?.length > 0 && (
                  <div className={classes.errorPanel}>
                    <div className={classes.errorTitle}>
                      <FileCopyIcon fontSize='small' style={{ display: 'none' }} />
                      Errors
                    </div>
                    <ul className={classes.errorList}>
                      {result.errors.map((err, idx) => (
                        <li key={idx}>
                          <span className={classes.errorPhase}>{err.phase || 'unknown'}</span>
                          {err.message}
                          {err.details && err.details !== err.message && (
                            <div style={{ marginTop: '4px', fontFamily: 'inherit', color: 'var(--color-danger-700, #b91c1c)' }}>
                              {err.details}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.warnings?.length > 0 && (
                  <div className={classes.warnPanel}>
                    {result.warnings.map((w, idx) => (
                      <div key={idx}>
                        <span style={{ fontWeight: 600, marginRight: '4px' }}>[{w.phase || 'warning'}]</span>
                        {w.message}
                      </div>
                    ))}
                  </div>
                )}

                <Section
                  title='Tables'
                  count={result.tableReferences?.length || 0}
                  isOpen={openSections.tables}
                  onToggle={() => toggleSection('tables')}
                >
                  {result.tableReferences?.length > 0
                    ? (
                      <div className={classes.chipGrid}>
                        {result.tableReferences.map((t, idx) => {
                          const fqn = t.schema ? `${t.schema}.${t.tableCode}` : t.tableCode
                          return (
                            <span key={idx} className={classes.chip}>
                              {fqn}
                              {t.alias && t.alias !== t.tableCode && (
                                <span className={classes.chipMuted}>AS {t.alias}</span>
                              )}
                            </span>
                          )
                        })}
                      </div>
                    )
                    : <div className={classes.emptyHint}>No tables resolved.</div>}
                </Section>

                <Section
                  title='Catalog matches'
                  count={result.catalogContext?.matchedViews?.length || 0}
                  isOpen={openSections.catalog}
                  onToggle={() => toggleSection('catalog')}
                >
                  {result.catalogContext?.matchedViews?.length > 0
                    ? (
                      <div className={classes.catalogList}>
                        {result.catalogContext.matchedViews.map((v, idx) => (
                          <CatalogMatch key={idx} match={v} />
                        ))}
                      </div>
                    )
                    : <div className={classes.emptyHint}>No catalog views matched. The fallback parser may be in use.</div>}
                </Section>

                <Section
                  title='Column references'
                  count={result.columnReferences?.length || 0}
                  isOpen={openSections.columns}
                  onToggle={() => toggleSection('columns')}
                >
                  {result.columnReferences?.length > 0
                    ? (
                      <div className={classes.chipGrid}>
                        {result.columnReferences.map((c, idx) => {
                          const t = c.table ? `${c.table}.` : ''
                          const a = c.alias && c.alias !== c.column ? ` AS ${c.alias}` : ''
                          const isFn = c.isAggregate || c.isFunction
                          return (
                            <span
                              key={idx}
                              className={`${classes.chip} ${isFn ? classes.chipAggregate : ''}`}
                            >
                              {t}{c.column}{a}
                            </span>
                          )
                        })}
                      </div>
                    )
                    : <div className={classes.emptyHint}>No column references extracted.</div>}
                </Section>

                {result.astColumnAnalysis && (
                  <Section
                    title='Unextracted columns'
                    count={result.astColumnAnalysis.unextractedCount}
                    isOpen={openSections.unextracted}
                    onToggle={() => toggleSection('unextracted')}
                  >
                    {result.astColumnAnalysis.unextractedCount > 0
                      ? (
                        <div>
                          <div className={classes.unextractedRow} style={{ fontWeight: 600, color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                            <span>type</span>
                            <span>alias</span>
                            <span>expression</span>
                          </div>
                          {result.astColumnAnalysis.allSelectColumns
                            .filter(c => !c.extracted)
                            .map((c, idx) => (
                              <div key={idx} className={classes.unextractedRow} title={c.extractionReason}>
                                <span>{c.expressionType}</span>
                                <span className={classes.chipMuted}>{c.alias || '—'}</span>
                                <span className={classes.unextractedExpr}>{c.rawExpression}</span>
                              </div>
                            ))}
                        </div>
                      )
                      : <div className={classes.emptyHint}>All select columns were captured by the extractor.</div>}
                  </Section>
                )}

                {result.recommendations?.length > 0 && (
                  <Section
                    title='Recommendations'
                    count={result.recommendations.length}
                    isOpen={openSections.recommendations}
                    onToggle={() => toggleSection('recommendations')}
                  >
                    <div className={classes.recList}>
                      {result.recommendations.map((r, idx) => (
                        <div key={idx} className={classes.recItem}>
                          <span className={`${classes.recPriority} ${
                            r.priority === 'high' ? classes.recPriorityHigh
                              : r.priority === 'medium' ? classes.recPriorityMedium
                                : classes.recPriorityLow
                          }`}
                          >
                            {r.priority || 'info'}
                          </span>
                          {r.description}
                          {r.file && (
                            <div style={{ marginTop: '4px', fontFamily: MONO, fontSize: '11px', color: 'var(--color-neutral-600)' }}>
                              {r.file}
                              {r.function ? ` · ${r.function}` : ''}
                              {r.line ? ` · line ${r.line}` : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {result.pipelineInfo?.sql && (
                  <Section
                    title='Compiled SQL'
                    isOpen={openSections.sql}
                    onToggle={() => toggleSection('sql')}
                  >
                    <pre className={classes.pre}>{result.pipelineInfo.sql}</pre>
                  </Section>
                )}

                {result.ast && (
                  <Section
                    title='Raw AST'
                    isOpen={openSections.ast}
                    onToggle={() => toggleSection('ast')}
                  >
                    <pre className={classes.pre}>{JSON.stringify(result.ast, null, 2)}</pre>
                  </Section>
                )}
              </>
            )}

            {!result && !analyzeError && !isAnalyzing && (
              <div className={classes.emptyHint}>
                Edit the DQL on the left or click Refresh to analyze.
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function Stat ({ label, value, tone }) {
  const classes = useStyles()
  let toneClass = ''
  if (tone === 'warn') toneClass = classes.statCardWarn
  else if (tone === 'ok') toneClass = classes.statCardOk
  return (
    <div className={classes.statCard}>
      <span className={classes.statCardLabel}>{label}</span>
      <span className={`${classes.statCardValue} ${toneClass}`}>{value}</span>
    </div>
  )
}

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tone: PropTypes.oneOf(['warn', 'ok'])
}

DqlDiagnosticsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  purposeCode: PropTypes.string,
  savedDql: PropTypes.string
}

export default DqlDiagnosticsModal
